import * as React from "react";
import { observer } from "mobx-react";
import { PackageFilterParams, PackagesProps, PackagesState } from "./typings";
import { ViewMedium, Viewport, ViewSmall } from "bernie-view";
import { flightClassId, PillOption, pillsConfig, starRatingId, tripLengthId } from "./config";
import { PillFilter } from "./components/PillFilter";
import { PackageCards } from "./PackageCards";
import { NoFilteredPackagesAvailableMessage } from "./NoFilteredPackagesAvailableMessage";
import { SeeAllButton } from "./components/PackageSeeAllButton";
import { PackagingFlexModuleResult } from "typings/microserviceModels/packaging-flex-module";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { logger } from "bernie-core";
import { UitkLink, UitkLinkTypeSize } from "uitk-react-link";
import { withStores } from "stores";
import { UitkHeading } from "uitk-react-text";

/**
 *
 * This component displays a list of Package Cards sourced through
 * lpt-flex-web -> package flex module -> DSE or Deals Syndication Engine. Check out DSE here:
 * https://confluence.expedia.biz/display/PDS/DSE+v2+-+DSE+Engine
 *
 * The component also supports a list of filters that can be applied to the query.
 *
 */
@logger("Packages")
@observer
export class Packages extends React.Component<PackagesProps, PackagesState> {
  private showPillFilter: boolean;
  constructor(props: PackagesProps) {
    super(props);
    const { flexModuleModelStore, flexTemplateNode } = props;

    if (!flexTemplateNode) {
      return;
    }

    const model = flexModuleModelStore.getModel(flexTemplateNode.id) as PackagingFlexModuleResult;
    this.showPillFilter = model.showPillFilter;
    if (model) {
      const { packages } = model;
      this.state = {
        hasInitialResult: packages?.length > 0,
        originCityName: packages?.[0]?.originCityName ?? "",
        hasMounted: false,
        lengthOfStay: "",
        starRating: "",
        flightClass: "",
      };
    }
  }

  public componentDidMount(): void {
    this.setState({
      hasMounted: true,
    });
  }

  private setPillParamState = (pillId: string, { lptParamValue }: PillOption, callbackFn: () => void) => {
    if (tripLengthId === pillId) {
      this.setState(
        {
          lengthOfStay: lptParamValue,
        },
        callbackFn
      );
    }
    if (starRatingId === pillId) {
      this.setState(
        {
          starRating: lptParamValue,
        },
        callbackFn
      );
    }
    if (flightClassId === pillId) {
      this.setState(
        {
          flightClass: lptParamValue,
        },
        callbackFn
      );
    }
  };

  private fetchPackages = (pillId: string, pillOption: PillOption) => {
    const { blossomComponent } = this.props;

    this.setPillParamState(pillId, pillOption, () => {
      const params: PackageFilterParams = {
        fmId: blossomComponent?.fmId || 0,
        lengthOfStay: this.state.lengthOfStay,
        starRating: this.state.starRating,
        flightClass: this.state.flightClass,
      };

      this.props.compositionStore.updateModule(params);
    });
  };

  private removeAllFilters = () => {
    this.setState({
      lengthOfStay: "",
      starRating: "",
      flightClass: "",
    });
    this.props.compositionStore.updateModule({ fmId: this.props.blossomComponent?.fmId || 0 });
    this.props.packagePillStore.reset();
  };

  public render() {
    const keyHelper = new ItemKeyHelper("Packages");
    const { flexModuleModelStore, flexTemplateNode, l10n, context } = this.props;
    const { originCityName, hasInitialResult } = this.state;
    const { formatText } = l10n;

    if (!flexTemplateNode) {
      return null;
    }

    const { id } = flexTemplateNode;
    const model = flexModuleModelStore.getModel(id) as PackagingFlexModuleResult;

    if (isPackageElementsEmpty(model) && !hasInitialResult) {
      return null;
    }

    const pillFilter = (
      <>
        {pillsConfig.map((pill) => (
          <PillFilter
            key={keyHelper.next()}
            pillOptions={pill.options}
            context={context}
            pillId={pill.pillId}
            fetchPackages={this.fetchPackages}
          />
        ))}

        <UitkLink type={UitkLinkTypeSize.SMALL} inline={true} onClick={this.removeAllFilters}>
          <button>{formatText("package.pills.removeFilters")}</button>
        </UitkLink>
      </>
    );

    return (
      <div className="Packages">
        <div className="PackagesCards uitk-cell all-cell-1-1 all-t-padding-three" id={id}>
          <UitkHeading size={600} level={2}>
            {model.title}
          </UitkHeading>

          {this.showPillFilter && pillFilter}

          {isPackageElementsEmpty(model) ? (
            <NoFilteredPackagesAvailableMessage
              headerText={formatText("maps.filter.error.filterError.header")}
              bodyText={formatText("maps.filter.error.filterError.message")}
            />
          ) : (
            <Viewport>
              <ViewSmall>
                <PackageCards
                  context={context}
                  packages={model.packages}
                  isMobile={true}
                  isFullWidth={true}
                  viewType={model.viewType}
                  showChooseYourDates={model.showChooseYourDates}
                />
              </ViewSmall>
              <ViewMedium>
                <PackageCards
                  context={context}
                  packages={model.packages}
                  isMobile={false}
                  isFullWidth={false}
                  viewType={model.viewType}
                  showChooseYourDates={model.showChooseYourDates}
                />
              </ViewMedium>
            </Viewport>
          )}
        </div>
        {model.showViewAllTrips && model.showChooseYourDates && <div className="Separator" />}
        {model.showViewAllTrips && (
          <SeeAllButton
            locationName={
              context.searchContext.location?.localizedName ?? context.searchContext.destination.localizedName
            }
            originCityName={originCityName}
            context={context}
            seeAllUrl={model.seeAllPackagesUrl}
            originName={model.originName}
          />
        )}
      </div>
    );
  }
}

const isPackageElementsEmpty = (model: PackagingFlexModuleResult) =>
  !model || model.empty || model.packages?.filter((pkg) => pkg).length === 0;

export default withStores("compositionStore", "packagePillStore", "flexModuleModelStore")(Packages);
